import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { convertYouTubeUrl, getYouTubeThumbnail, getFileUrl, getResources, createResource, updateResource, deleteResource, uploadImage, uploadVideo, uploadResourceFile } from '../../services/apiService';

const Resources = () => {
  const [userEmail, setUserEmail] = useState('');
  const [resources, setResources] = useState([]);
  const [editingResource, setEditingResource] = useState(null);
  const [showResourceForm, setShowResourceForm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('date'); // 'date', 'title', 'downloads', 'status'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'
  const [resourceToDelete, setResourceToDelete] = useState(null); // Pour la popup de suppression
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null); // Pour la notification toast
  const navigate = useNavigate();

  // Fonction pour afficher les notifications
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => {
      setNotification(null);
    }, 3000);
  };

  // Fonction pour transformer les données de l'API vers le format frontend
  const transformResourceData = (resource) => {
    const createdAt = resource.created_at ? resource.created_at.split('T')[0] : (resource.createdAt || resource.date || '');
    const updatedAt = resource.updated_at ? resource.updated_at.split('T')[0] : (resource.updatedAt || '');
    
    return {
      ...resource,
      type: resource.file_type || resource.type || '',
      fileUrl: resource.file_url || resource.file_path || resource.fileUrl || '',
      date: createdAt,
      createdAt: createdAt,
      updatedAt: updatedAt && updatedAt !== createdAt ? updatedAt : null,
      author: resource.author_name || resource.author || 'Admin'
    };
  };

  // Fonction pour charger les ressources depuis l'API
  const fetchResources = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getResources();
      // Transformer les données pour correspondre au format attendu
      const transformedData = data.map(transformResourceData);
      setResources(transformedData);
    } catch (err) {
      setError('Erreur lors du chargement des ressources');
      setResources([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('isLoggedIn');
    const email = localStorage.getItem('userEmail');
    
    if (!isLoggedIn || !email) {
      navigate('/login');
      return;
    }
    
    setUserEmail(email);
    fetchResources();
  }, [navigate]);

  const handleResourceSubmit = async (resourceData) => {
    try {
      let fileUrl = null;
      let filePath = null;
      let fileSourceType = 'url';
      let fileName = null;
      let fileSize = null;

      // Uploader le fichier si c'est un upload
      if (resourceData.fileUrlType === 'upload' && resourceData.uploadedFile) {
        try {
          // Déterminer le type de fichier
          const isVideo = resourceData.type === 'Video';
          const isImage = resourceData.uploadedFile.type && resourceData.uploadedFile.type.startsWith('image/');
          
          let uploadedFile = null;
          if (isVideo) {
            uploadedFile = await uploadVideo(resourceData.uploadedFile);
          } else if (isImage) {
            uploadedFile = await uploadImage(resourceData.uploadedFile);
          } else {
            // Pour tous les autres types de fichiers (PDF, Word, Excel, etc.)
            uploadedFile = await uploadResourceFile(resourceData.uploadedFile);
          }
          
          if (uploadedFile) {
            filePath = uploadedFile.path;
            fileName = uploadedFile.filename;
            fileSize = uploadedFile.size;
            fileSourceType = 'upload';
          }
        } catch (error) {
          showNotification('Error uploading file: ' + (error.message || 'Unknown error'), 'error');
          return;
        }
      } else if (resourceData.fileUrlType === 'link') {
        fileUrl = resourceData.fileUrl || null;
        fileSourceType = 'url';
      }

      // Préparer les données pour l'API
      const apiData = {
        title: resourceData.title.trim(),
        description: resourceData.description || null,
        content: resourceData.content || null,
        file_url: fileUrl,
        file_source_type: fileSourceType,
        file_path: filePath,
        file_name: fileName,
        file_size: fileSize,
        file_type: resourceData.type || null,
        status: resourceData.status || 'Brouillon',
        author_id: 1, // TODO: Récupérer l'ID de l'utilisateur connecté
        downloads: editingResource ? editingResource.downloads || 0 : 0
      };

      let savedResource;
      if (editingResource) {
        // Mettre à jour
        savedResource = await updateResource(editingResource.id, apiData);
        if (savedResource) {
          await fetchResources();
          setShowResourceForm(false);
          setEditingResource(null);
          showNotification('Resource updated successfully', 'success');
        } else {
          showNotification('Error updating resource', 'error');
        }
      } else {
        // Créer
        savedResource = await createResource(apiData);
        if (savedResource) {
          await fetchResources();
          setShowResourceForm(false);
          setEditingResource(null);
          showNotification('Resource created successfully', 'success');
        } else {
          showNotification('Error creating resource', 'error');
        }
      }
      } catch (error) {
        showNotification('Error saving: ' + (error.message || 'Unknown error'), 'error');
    }
  };

  const handleDeleteResource = (id) => {
    const item = resources.find(r => r.id === id);
    if (item) {
      setResourceToDelete(item);
    }
  };

  const confirmDeleteResource = async () => {
    if (resourceToDelete) {
      try {
        const success = await deleteResource(resourceToDelete.id);
        if (success) {
          await fetchResources();
          setResourceToDelete(null);
          showNotification('Resource deleted successfully', 'success');
        } else {
          showNotification('Error deleting resource', 'error');
        }
      } catch (error) {
        showNotification('Error deleting: ' + (error.message || 'Unknown error'), 'error');
      }
    }
  };

  const handleEditResource = (item) => {
    setEditingResource(item);
    setShowResourceForm(true);
  };

  // Filtrage des ressources
  const filteredResources = resources.filter(item => {
    // Filtre par recherche (titre ou description)
    const matchesSearch = searchTerm === '' || 
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    // Filtre par date (createdAt ou updatedAt)
    const itemDate = item.created_at ? item.created_at.split('T')[0] : (item.createdAt || item.date || '');
    const matchesDate = dateFilter === '' || 
      itemDate === dateFilter || 
      (item.updated_at && item.updated_at.split('T')[0] === dateFilter) ||
      (item.updatedAt && item.updatedAt === dateFilter);
    
    // Filtre par type (file_type ou type)
    const resourceType = item.file_type || item.type || '';
    const matchesType = typeFilter === '' || resourceType === typeFilter;

    // Filter by status (keep stored values like "Publié/Brouillon")
    const matchesStatus = statusFilter === '' || item.status === statusFilter;
    
    return matchesSearch && matchesDate && matchesType && matchesStatus;
  });

  // Tri des ressources filtrées
  const sortedResources = [...filteredResources].sort((a, b) => {
    let comparison = 0;
    
    switch (sortBy) {
      case 'date':
        const dateA = a.updated_at || a.updatedAt || a.created_at || a.createdAt || a.date || '';
        const dateB = b.updated_at || b.updatedAt || b.created_at || b.createdAt || b.date || '';
        comparison = new Date(dateA) - new Date(dateB);
        break;
      case 'title':
        comparison = a.title.localeCompare(b.title, 'fr', { sensitivity: 'base' });
        break;
      case 'downloads':
        comparison = (a.downloads || 0) - (b.downloads || 0);
        break;
      case 'status':
        comparison = a.status.localeCompare(b.status, 'fr', { sensitivity: 'base' });
        break;
      default:
        comparison = 0;
    }
    
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  // Réinitialiser la page à 1 lors du changement de filtre ou tri
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, dateFilter, typeFilter, statusFilter, sortBy, sortOrder]);

  const getStatusLabel = (status) => {
    if (status === 'Publié') return 'Published';
    if (status === 'Brouillon') return 'Draft';
    return status || '';
  };

  const allStatuses = Array.from(new Set(resources.map(r => r.status).filter(Boolean))).sort();

  // Pagination basée sur les ressources filtrées et triées
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentResources = sortedResources.slice(startIndex, endIndex);
  const totalPages = Math.ceil(sortedResources.length / itemsPerPage);

  // Obtenir tous les types uniques
  const allTypes = Array.from(new Set(
    resources.map(item => item.file_type || item.type).filter(Boolean)
  )).sort();

  const ResourceForm = ({ resource, onSubmit, onCancel }) => {
    // Fonction pour générer un nom de fichier unique
    const generateFileName = (originalFileName, type) => {
      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substring(2, 9);
      const extension = originalFileName.split('.').pop();
      const prefix = type === 'PDF' ? 'pdf' : type === 'Excel' ? 'xls' : type === 'Word' ? 'doc' : type === 'Video' ? 'vid' : type === 'Link' ? 'link' : 'file';
      return `${prefix}_${timestamp}_${randomId}.${extension}`;
    };

    // Fonction pour obtenir le chemin relatif du fichier
    const getFileRelativePath = (fileName, type) => {
      return `/aseets/images/Admin/Resources/${fileName}`;
    };

    const [formData, setFormData] = useState({
      title: resource?.title || '',
      type: resource?.type || '',
      fileUrl: resource?.fileUrl || '',
      fileUrlType: resource?.type === 'Link' ? 'link' : 'link', // 'link' or 'upload'
      uploadedFile: null,
      // Keep backend status values (French) but display English in UI
      status: resource?.status || 'Brouillon'
    });

    const [errors, setErrors] = useState({
      title: '',
      type: '',
      fileUrl: '',
      status: ''
    });
    const [isSubmitted, setIsSubmitted] = useState(false);

    useEffect(() => {
      if (resource) {
        // Déterminer le type de source (upload ou link)
        const resourceType = resource.file_type || resource.type || '';
        // Si le type est "Link", forcer fileUrlType à 'link'
        const fileUrlType = resourceType === 'Link' ? 'link' : (resource.file_path ? 'upload' : (resource.file_url ? 'link' : 'link'));
        // Construire l'URL du fichier
        const fileUrl = resource.file_url || (resource.file_path ? getFileUrl(resource.file_path) : '') || resource.fileUrl || '';
        
        setFormData({
          title: resource.title || '',
          description: resource.description || '',
          content: resource.content || '',
          type: resourceType,
          fileUrl: fileUrl,
          fileUrlType: fileUrlType,
          uploadedFile: null, // Pas de fichier pour les ressources existantes
          status: resource.status || 'Brouillon'
        });
      }
    }, [resource]);

    const handleFileUpload = async (e) => {
      const file = e.target.files[0];
      if (file) {
        // Définir les tailles maximales selon le type de fichier
        const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB pour les fichiers généraux
        const MAX_VIDEO_SIZE = 100 * 1024 * 1024; // 100 MB pour les vidéos
        const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10 MB pour les images
        
        // Déterminer la taille maximale selon le type
        let maxSize = MAX_FILE_SIZE;
        let maxSizeLabel = '50 MB';
        
        if (formData.type === 'Video' || file.type.startsWith('video/')) {
          maxSize = MAX_VIDEO_SIZE;
          maxSizeLabel = '100 MB';
        } else if (file.type.startsWith('image/')) {
          maxSize = MAX_IMAGE_SIZE;
          maxSizeLabel = '10 MB';
        }
        
        // Vérifier la taille du fichier
        if (file.size > maxSize) {
          const fileSizeMB = (file.size / 1024 / 1024).toFixed(2);
          setErrors(prev => ({
            ...prev,
            fileUrl: `File size (${fileSizeMB} MB) exceeds the allowed limit (${maxSizeLabel})`
          }));
          // Réinitialiser le champ de fichier
          e.target.value = '';
          setIsSubmitted(true); // Marquer comme soumis pour afficher l'erreur
          return;
        }
        
        // Générer un nom de fichier unique basé sur le type de ressource
        const fileName = generateFileName(file.name, formData.type);
        const relativePath = getFileRelativePath(fileName, formData.type);
        
        // Pour l'instant, on lit le fichier en base64 pour l'aperçu (si c'est une image ou vidéo)
        if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
          const reader = new FileReader();
          reader.onloadend = () => {
            setFormData(prev => ({
              ...prev,
              uploadedFile: reader.result,
              fileUrl: reader.result, // Base64 pour l'aperçu
              uploadedFileName: fileName, // Nom du fichier qui sera sauvegardé
              uploadedFileSize: (file.size / 1024 / 1024).toFixed(2) + ' MB',
              uploadedFilePath: relativePath // Chemin relatif du fichier
            }));
            // Réinitialiser l'erreur de fichier quand un fichier est uploadé
            setErrors(prev => ({ ...prev, fileUrl: '' }));
          };
          reader.readAsDataURL(file);
        } else {
          // Pour les autres types de fichiers (PDF, Word, Excel), on stocke juste les infos
          setFormData(prev => ({
            ...prev,
            uploadedFile: file,
            fileUrl: file.name, // Nom du fichier pour l'affichage
            uploadedFileName: fileName, // Nom du fichier qui sera sauvegardé
            uploadedFileSize: (file.size / 1024 / 1024).toFixed(2) + ' MB',
            uploadedFilePath: relativePath // Chemin relatif du fichier
          }));
          // Réinitialiser l'erreur de fichier quand un fichier est uploadé
          setErrors(prev => ({ ...prev, fileUrl: '' }));
        }
      }
    };

    const validateForm = () => {
      const newErrors = {
        title: '',
        type: '',
        fileUrl: '',
        status: ''
      };
      let isValid = true;

      // Valider le titre
      if (!formData.title || formData.title.trim() === '') {
        newErrors.title = 'Title is required';
        isValid = false;
      }

      // Valider le type
      if (!formData.type || formData.type.trim() === '') {
        newErrors.type = 'Type is required';
        isValid = false;
      }

      // Valider l'URL du fichier
      if (formData.fileUrlType === 'link') {
        if (!formData.fileUrl || formData.fileUrl.trim() === '') {
          newErrors.fileUrl = 'File URL is required';
          isValid = false;
        }
      } else if (formData.fileUrlType === 'upload') {
        if (!formData.uploadedFile && !formData.fileUrl) {
          newErrors.fileUrl = 'File is required';
          isValid = false;
        }
      } else {
        // Si aucun type n'est défini ou si le fichier est vide
        if (!formData.fileUrl || formData.fileUrl.trim() === '') {
          newErrors.fileUrl = 'File URL is required';
          isValid = false;
        }
      }

      // Valider le statut
      if (!formData.status || formData.status.trim() === '') {
        newErrors.status = 'Status is required';
        isValid = false;
      }

      setErrors(newErrors);
      return isValid;
    };

    const handleSubmit = (e) => {
      e.preventDefault();
      
      // Marquer comme soumis AVANT la validation pour afficher les erreurs
      setIsSubmitted(true);
      
      // Valider le formulaire (cela va mettre à jour les erreurs)
      const isValid = validateForm();
      
      if (isValid) {
        onSubmit(formData);
        setIsSubmitted(false);
        // Réinitialiser les erreurs après soumission réussie
        setErrors({
          title: '',
          type: '',
          fileUrl: '',
          status: ''
        });
      }
    };

    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: window.innerWidth <= 768 ? '10px' : '20px',
          paddingTop: window.innerWidth <= 768 ? '74px' : '20px',
          animation: 'fadeIn 0.2s ease-in',
          overflowY: 'auto'
        }}
      >
        <style>{`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideUp {
            from { transform: translateY(20px); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }
          @media (max-width: 768px) {
            .resource-form-container {
              max-width: 100% !important;
              border-radius: 12px !important;
              max-height: 95vh !important;
            }
            .resource-form-header {
              padding: 16px !important;
              border-radius: 12px 12px 0 0 !important;
            }
            .resource-form-content {
              padding: 20px 16px !important;
            }
          }
        `}</style>
        <div
          className="resource-form-container"
          onClick={(e) => e.stopPropagation()}
          style={{
            background: 'white',
            borderRadius: window.innerWidth <= 768 ? '12px' : '20px',
            maxWidth: window.innerWidth <= 768 ? '100%' : '900px',
            width: '100%',
            maxHeight: window.innerWidth <= 768 ? '95vh' : '90vh',
            overflowY: 'auto',
            position: 'relative',
            boxShadow: '0 25px 80px rgba(0, 0, 0, 0.4)',
            animation: 'slideUp 0.3s ease-out'
          }}
        >
          {/* Header avec gradient */}
          <div 
            className="resource-form-header"
            style={{
              background: 'linear-gradient(135deg, rgb(150, 202, 45) 0%, rgb(127, 184, 0) 100%)',
              padding: window.innerWidth <= 768 ? '16px' : '24px 24px 20px 24px',
              borderRadius: window.innerWidth <= 768 ? '12px 12px 0 0' : '20px 20px 0 0',
              position: 'sticky',
              top: 0,
              zIndex: 10,
              boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
            }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ 
                margin: 0,
                fontSize: window.innerWidth <= 768 ? '18px' : '24px',
                fontWeight: 700,
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <i className={`fas ${resource ? 'fa-edit' : 'fa-plus-circle'}`} style={{ fontSize: window.innerWidth <= 768 ? '16px' : '22px' }} />
                {resource ? 'Edit' : 'New'} Resource
              </h2>
              <button
                onClick={onCancel}
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '18px',
                  transition: 'all 0.3s ease',
                  backdropFilter: 'blur(10px)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                  e.currentTarget.style.transform = 'rotate(90deg)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.transform = 'rotate(0deg)';
                }}
              >
                <i className="fas fa-times" />
              </button>
            </div>
          </div>

                  {/* Form content */}
          <div className="resource-form-content" style={{ padding: window.innerWidth <= 768 ? '20px 16px' : '32px' }}>
            <form onSubmit={handleSubmit}>
              {/* Title */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '10px', 
                  fontWeight: 600,
                  color: '#1f2937',
                  fontSize: '14px'
                }}>
                  <i className="fas fa-heading" style={{ color: '#96ca2d', fontSize: '14px' }} />
                  Title *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setFormData({ ...formData, title: newValue });
                    if (newValue.trim() !== '' && errors.title) {
                      setErrors({ ...errors, title: '' });
                    }
                  }}
                  placeholder="Enter the resource title"
                  style={{ 
                    width: '100%', 
                    padding: '12px 16px', 
                    border: (isSubmitted && errors.title) ? '2px solid #ef4444' : '2px solid #e5e7eb', 
                    borderRadius: '10px',
                    fontSize: '15px',
                    transition: 'all 0.3s ease',
                    outline: 'none'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#96ca2d';
                    e.target.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.1)';
                  }}
                  onBlur={(e) => {
                    if (!isSubmitted || !errors.title) {
                      e.target.style.borderColor = '#e5e7eb';
                    }
                    e.target.style.boxShadow = 'none';
                  }}
                />
                {isSubmitted && errors.title && (
                  <div style={{
                    marginTop: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <i className="fas fa-exclamation-circle" style={{ 
                      color: '#ef4444', 
                      fontSize: '12px',
                      flexShrink: 0
                    }} />
                    <span style={{ 
                      color: '#dc2626', 
                      fontSize: '12px',
                      fontWeight: 500
                    }}>
                      {errors.title}
                    </span>
                  </div>
                )}
              </div>

              {/* Type */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '10px', 
                  fontWeight: 600,
                  color: '#1f2937',
                  fontSize: '14px'
                }}>
                  <i className="fas fa-file-alt" style={{ color: '#96ca2d', fontSize: '14px' }} />
                  Type *
                </label>
                <div style={{ 
                  position: 'relative',
                  background: 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)',
                  borderRadius: '12px',
                  border: (isSubmitted && errors.type) ? '2px solid #ef4444' : '2px solid #e5e7eb',
                  transition: 'all 0.3s ease',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  const selectElement = e.currentTarget.querySelector('select');
                  if (document.activeElement !== selectElement) {
                    e.currentTarget.style.borderColor = '#96ca2d';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(150, 202, 45, 0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  const selectElement = e.currentTarget.querySelector('select');
                  if (document.activeElement !== selectElement) {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.boxShadow = 'none';
                  }
                }}
                >
                  <select
                    name="type"
                    value={formData.type}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      // Si le type est "Link", forcer fileUrlType à 'link'
                      const newFileUrlType = newValue === 'Link' ? 'link' : formData.fileUrlType;
                      setFormData({ ...formData, type: newValue, fileUrlType: newFileUrlType });
                      if (newValue.trim() !== '' && errors.type) {
                        setErrors({ ...errors, type: '' });
                      }
                    }}
                    style={{ 
                      width: '100%', 
                      padding: '10px 45px 10px 14px', 
                      border: 'none',
                      borderRadius: '12px',
                      background: 'transparent',
                      fontSize: '15px',
                      cursor: 'pointer',
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      MozAppearance: 'none',
                      fontWeight: 500,
                      color: '#1f2937',
                      lineHeight: '1.5'
                    }}
                    onFocus={(e) => {
                      const container = e.target.parentElement;
                      container.style.borderColor = '#96ca2d';
                      container.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.15)';
                      container.style.background = 'linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%)';
                      const arrow = container.querySelector('.select-arrow');
                      if (arrow) arrow.style.transform = 'translateY(-50%) rotate(180deg)';
                    }}
                    onBlur={(e) => {
                      const container = e.target.parentElement;
                      if (!isSubmitted || !errors.type) {
                        container.style.borderColor = '#e5e7eb';
                      }
                      container.style.boxShadow = 'none';
                      container.style.background = 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)';
                      const arrow = container.querySelector('.select-arrow');
                      if (arrow) arrow.style.transform = 'translateY(-50%) rotate(0deg)';
                    }}
                    onMouseDown={(e) => {
                      const container = e.target.parentElement;
                      const arrow = container.querySelector('.select-arrow');
                      if (arrow) arrow.style.transform = 'translateY(-50%) rotate(180deg)';
                    }}
                  >
                    <option value="">Select a type *</option>
                    <option value="PDF">📄 PDF</option>
                    <option value="Excel">📊 Excel</option>
                    <option value="Word">📝 Word</option>
                    <option value="Video">🎥 Video</option>
                    <option value="Link">🔗 Link</option>
                  </select>
                  <div 
                    className="select-arrow"
                    style={{
                      position: 'absolute',
                      right: '18px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      pointerEvents: 'none',
                      color: '#96ca2d',
                      fontSize: '16px',
                      transition: 'transform 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      background: 'rgba(150, 202, 45, 0.1)'
                    }}
                  >
                    <i className="fas fa-chevron-down" />
                  </div>
                </div>
                {isSubmitted && errors.type && (
                  <div style={{
                    marginTop: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <i className="fas fa-exclamation-circle" style={{ 
                      color: '#ef4444', 
                      fontSize: '12px',
                      flexShrink: 0
                    }} />
                    <span style={{ 
                      color: '#dc2626', 
                      fontSize: '12px',
                      fontWeight: 500
                    }}>
                      {errors.type}
                    </span>
                  </div>
                )}
              </div>

              {/* File URL */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '12px', 
                  fontWeight: 600,
                  color: '#1f2937',
                  fontSize: '14px'
                }}>
                  <i className="fas fa-file" style={{ color: '#96ca2d', fontSize: '14px' }} />
                  File *
                </label>
                <div style={{ 
                  padding: '16px',
                  border: (isSubmitted && errors.fileUrl) ? '2px dashed #ef4444' : '2px dashed #e5e7eb',
                  borderRadius: '12px',
                  background: '#f9fafb',
                  transition: 'all 0.3s ease'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    gap: window.innerWidth <= 768 ? '8px' : '10px', 
                    marginBottom: '16px',
                    marginRight: '0',
                    flexDirection: window.innerWidth <= 768 ? 'column' : 'row'
                  }}>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, fileUrlType: 'link' })}
                      style={{
                        flex: 1,
                        padding: window.innerWidth <= 768 ? '12px 16px' : '12px 20px',
                        background: formData.fileUrlType === 'link' 
                          ? 'linear-gradient(135deg, #96ca2d 0%, #7ba818 100%)' 
                          : '#ffffff',
                        color: formData.fileUrlType === 'link' ? 'white' : '#374151',
                        border: formData.fileUrlType === 'link' ? 'none' : '2px solid #e5e7eb',
                        borderRadius: '10px',
                        cursor: 'pointer',
                        fontSize: window.innerWidth <= 768 ? '13px' : '14px',
                        fontWeight: 600,
                        transition: 'all 0.3s ease',
                        boxShadow: formData.fileUrlType === 'link' ? '0 4px 12px rgba(150, 202, 45, 0.3)' : 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                      onMouseEnter={(e) => {
                        if (formData.fileUrlType !== 'link') {
                          e.currentTarget.style.borderColor = '#96ca2d';
                          e.currentTarget.style.transform = 'translateY(-2px)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (formData.fileUrlType !== 'link') {
                          e.currentTarget.style.borderColor = '#e5e7eb';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }
                      }}
                    >
                      <i className="fas fa-link" />
                      URL link
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, fileUrlType: 'upload' })}
                      disabled={formData.type === 'Link'}
                      style={{
                        flex: 1,
                        padding: window.innerWidth <= 768 ? '12px 16px' : '12px 20px',
                        background: formData.fileUrlType === 'upload' 
                          ? 'linear-gradient(135deg, #96ca2d 0%, #7ba818 100%)' 
                          : '#ffffff',
                        color: formData.fileUrlType === 'upload' ? 'white' : '#374151',
                        border: formData.fileUrlType === 'upload' ? 'none' : '2px solid #e5e7eb',
                        borderRadius: '10px',
                        cursor: formData.type === 'Link' ? 'not-allowed' : 'pointer',
                        fontSize: window.innerWidth <= 768 ? '13px' : '14px',
                        fontWeight: 600,
                        transition: 'all 0.3s ease',
                        boxShadow: formData.fileUrlType === 'upload' ? '0 4px 12px rgba(150, 202, 45, 0.3)' : 'none',
                        opacity: formData.type === 'Link' ? 0.5 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                      onMouseEnter={(e) => {
                        if (formData.fileUrlType !== 'upload') {
                          e.currentTarget.style.borderColor = '#96ca2d';
                          e.currentTarget.style.transform = 'translateY(-2px)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (formData.fileUrlType !== 'upload') {
                          e.currentTarget.style.borderColor = '#e5e7eb';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }
                      }}
                    >
                      <i className="fas fa-cloud-upload-alt" />
                      Upload file
                    </button>
                  </div>
                  {formData.fileUrlType === 'link' ? (
                    <input
                      type="text"
                      value={formData.fileUrl}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setFormData({ ...formData, fileUrl: newValue });
                        if (newValue.trim() !== '' && errors.fileUrl) {
                          setErrors({ ...errors, fileUrl: '' });
                        }
                      }}
                      placeholder="/resources/file.pdf or https://..."
                      style={{ 
                        width: '100%', 
                        padding: '12px 16px', 
                        border: (isSubmitted && errors.fileUrl) ? '2px solid #ef4444' : '2px solid #e5e7eb', 
                        borderRadius: '10px',
                        fontSize: '14px',
                        transition: 'all 0.3s ease',
                        outline: 'none'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#96ca2d';
                        e.target.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.1)';
                      }}
                      onBlur={(e) => {
                        if (!isSubmitted || !errors.fileUrl) {
                          e.target.style.borderColor = '#e5e7eb';
                        }
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  ) : (
                    <div>
                      <label 
                        htmlFor="resource-file-upload"
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '4px 8px',
                          border: '3px dashed #96ca2d',
                          borderRadius: '12px',
                          background: 'linear-gradient(135deg, rgba(150, 202, 45, 0.08) 0%, rgba(150, 202, 45, 0.03) 100%)',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          position: 'relative',
                          overflow: 'hidden'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'linear-gradient(135deg, rgba(150, 202, 45, 0.15) 0%, rgba(150, 202, 45, 0.08) 100%)';
                          e.currentTarget.style.borderColor = '#7ba818';
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 8px 20px rgba(150, 202, 45, 0.2)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'linear-gradient(135deg, rgba(150, 202, 45, 0.08) 0%, rgba(150, 202, 45, 0.03) 100%)';
                          e.currentTarget.style.borderColor = '#96ca2d';
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        <input
                          type="file"
                          accept={formData.type === 'PDF' ? '.pdf' : formData.type === 'Excel' ? '.xlsx,.xls' : formData.type === 'Word' ? '.doc,.docx' : formData.type === 'Video' ? 'video/*' : '*'}
                          onChange={handleFileUpload}
                          style={{ display: 'none' }}
                          id="resource-file-upload"
                        />
                        <div style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #96ca2d 0%, #7ba818 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginBottom: '4px',
                          boxShadow: '0 2px 6px rgba(150, 202, 45, 0.3)'
                        }}>
                          <i className="fas fa-cloud-upload-alt" style={{ fontSize: '14px', color: 'white' }} />
                        </div>
                        <span style={{ 
                          color: '#1f2937', 
                          fontWeight: 600, 
                          fontSize: '11px',
                          marginBottom: '2px'
                        }}>
                          Click to select a file
                        </span>
                      </label>
                      {formData.fileUrlType === 'upload' && formData.type !== 'Link' && (
                        <div style={{
                          marginTop: '8px',
                          padding: '8px 12px',
                          background: '#fef3c7',
                          border: '1px solid #fbbf24',
                          borderRadius: '6px',
                          fontSize: '12px',
                          color: '#92400e',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          <i className="fas fa-exclamation-triangle" style={{ fontSize: '12px' }} />
                          <span>File size must not exceed 50MB</span>
                        </div>
                      )}
                      {formData.uploadedFile && (
                        <div style={{ marginTop: '16px' }}>
                          <div style={{
                            padding: '12px 16px',
                            background: 'linear-gradient(135deg, rgba(150, 202, 45, 0.1) 0%, rgba(150, 202, 45, 0.05) 100%)',
                            borderRadius: '10px',
                            border: '2px solid #96ca2d',
                            marginBottom: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px'
                          }}>
                            <div style={{
                              width: '40px',
                              height: '40px',
                              borderRadius: '50%',
                              background: 'linear-gradient(135deg, #96ca2d 0%, #7ba818 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}>
                              <i className="fas fa-check" style={{ color: 'white', fontSize: '14px' }} />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ 
                                fontWeight: 600, 
                                color: '#1f2937', 
                                fontSize: '14px',
                                marginBottom: '4px'
                              }}>
                                {formData.uploadedFileName || formData.fileUrl}
                              </div>
                              {formData.uploadedFileSize && (
                                <div style={{ 
                                  color: '#6b7280', 
                                  fontSize: '12px' 
                                }}>
                                  {formData.uploadedFileSize}
                                </div>
                              )}
                            </div>
                          </div>
                          {(formData.type === 'Video' || (formData.uploadedFile && typeof formData.uploadedFile === 'string' && formData.uploadedFile.startsWith('data:image'))) && (
                            <div style={{ textAlign: 'center' }}>
                              {formData.type === 'Video' ? (
                                (() => {
                                  const videoUrl = formData.uploadedFile || formData.fileUrl;
                                  const isYouTube = videoUrl && (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be'));
                                  
                                  return isYouTube ? (
                                    <iframe
                                      src={convertYouTubeUrl(videoUrl)}
                                      width="100%"
                                      height="250"
                                      frameBorder="0"
                                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                      allowFullScreen
                                      style={{ 
                                        borderRadius: '12px', 
                                        border: '2px solid #e5e7eb',
                                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                                      }}
                                    />
                                  ) : (
                                    <video 
                                      src={videoUrl} 
                                      controls
                                      style={{ 
                                        maxWidth: '100%', 
                                        maxHeight: '250px', 
                                        borderRadius: '12px', 
                                        border: '2px solid #e5e7eb',
                                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                                      }}
                                      onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                  );
                                })()
                              ) : (
                                <img 
                                  src={formData.uploadedFile} 
                                  alt="Preview" 
                                  style={{ 
                                    maxWidth: '100%', 
                                    maxHeight: '250px', 
                                    borderRadius: '12px', 
                                    border: '2px solid #e5e7eb',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                                  }}
                                />
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {formData.fileUrl && formData.fileUrlType === 'link' && (
                    <div style={{ marginTop: '16px', textAlign: 'center' }}>
                      {formData.type === 'Video' && (formData.fileUrl.includes('youtube.com') || formData.fileUrl.includes('youtu.be')) ? (
                        <div style={{ marginTop: '16px' }}>
                          <iframe
                            src={convertYouTubeUrl(formData.fileUrl)}
                            width="100%"
                            height="250"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            style={{ 
                              borderRadius: '12px', 
                              border: '2px solid #e5e7eb',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                            }}
                          />
                        </div>
                      ) : (
                        <div style={{
                          padding: '12px 16px',
                          background: 'linear-gradient(135deg, rgba(150, 202, 45, 0.1) 0%, rgba(150, 202, 45, 0.05) 100%)',
                          borderRadius: '10px',
                          border: '2px solid #96ca2d',
                          display: 'inline-block'
                        }}>
                          <i className="fas fa-link" style={{ color: '#96ca2d', marginRight: '8px' }} />
                          <span style={{ color: '#1f2937', fontWeight: 600 }}>
                            {formData.fileUrl.length > 50 ? formData.fileUrl.substring(0, 50) + '...' : formData.fileUrl}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {isSubmitted && errors.fileUrl && (
                  <div style={{
                    marginTop: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <i className="fas fa-exclamation-circle" style={{ 
                      color: '#ef4444', 
                      fontSize: '12px',
                      flexShrink: 0
                    }} />
                    <span style={{ 
                      color: '#dc2626', 
                      fontSize: '12px',
                      fontWeight: 500
                    }}>
                      {errors.fileUrl}
                    </span>
                  </div>
                )}
              </div>

              {/* Status */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '10px', 
                  fontWeight: 600,
                  color: '#1f2937',
                  fontSize: '14px'
                }}>
                  <i className="fas fa-toggle-on" style={{ color: '#96ca2d', fontSize: '14px' }} />
                  Status *
                </label>
                <div style={{ 
                  position: 'relative',
                  background: 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)',
                  borderRadius: '12px',
                  border: (isSubmitted && errors.status) ? '2px solid #ef4444' : '2px solid #e5e7eb',
                  transition: 'all 0.3s ease',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  const selectElement = e.currentTarget.querySelector('select');
                  if (document.activeElement !== selectElement) {
                    e.currentTarget.style.borderColor = '#96ca2d';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(150, 202, 45, 0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  const selectElement = e.currentTarget.querySelector('select');
                  if (document.activeElement !== selectElement) {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.boxShadow = 'none';
                  }
                }}
                >
                  <select
                    name="status"
                    value={formData.status}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setFormData({ ...formData, status: newValue });
                      if (newValue.trim() !== '' && errors.status) {
                        setErrors({ ...errors, status: '' });
                      }
                    }}
                    style={{ 
                      width: '100%', 
                      padding: '10px 45px 10px 14px', 
                      border: 'none',
                      borderRadius: '12px',
                      background: 'transparent',
                      fontSize: '15px',
                      cursor: 'pointer',
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      MozAppearance: 'none',
                      fontWeight: 500,
                      color: '#1f2937',
                      lineHeight: '1.5'
                    }}
                    onFocus={(e) => {
                      const container = e.target.parentElement;
                      container.style.borderColor = '#96ca2d';
                      container.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.15)';
                      container.style.background = 'linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%)';
                      const arrow = container.querySelector('.select-arrow');
                      if (arrow) arrow.style.transform = 'translateY(-50%) rotate(180deg)';
                    }}
                    onBlur={(e) => {
                      const container = e.target.parentElement;
                      if (!isSubmitted || !errors.status) {
                        container.style.borderColor = '#e5e7eb';
                      }
                      container.style.boxShadow = 'none';
                      container.style.background = 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)';
                      const arrow = container.querySelector('.select-arrow');
                      if (arrow) arrow.style.transform = 'translateY(-50%) rotate(0deg)';
                    }}
                    onMouseDown={(e) => {
                      const container = e.target.parentElement;
                      const arrow = container.querySelector('.select-arrow');
                      if (arrow) arrow.style.transform = 'translateY(-50%) rotate(180deg)';
                    }}
                  >
                    <option value="Brouillon" style={{ padding: '10px', background: '#ffffff' }}>📝 Draft</option>
                    <option value="Publié" style={{ padding: '10px', background: '#ffffff' }}>✅ Published</option>
                  </select>
                  <div 
                    className="select-arrow"
                    style={{
                      position: 'absolute',
                      right: '18px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      pointerEvents: 'none',
                      color: '#96ca2d',
                      fontSize: '16px',
                      transition: 'transform 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      background: 'rgba(150, 202, 45, 0.1)'
                    }}
                  >
                    <i className="fas fa-chevron-down" />
                  </div>
                </div>
                {isSubmitted && errors.status && (
                  <div style={{
                    marginTop: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <i className="fas fa-exclamation-circle" style={{ 
                      color: '#ef4444', 
                      fontSize: '12px',
                      flexShrink: 0
                    }} />
                    <span style={{ 
                      color: '#dc2626', 
                      fontSize: '12px',
                      fontWeight: 500
                    }}>
                      {errors.status}
                    </span>
                  </div>
                )}
              </div>

              {/* Boutons d'action */}
              <div style={{ 
                display: 'flex', 
                gap: window.innerWidth <= 768 ? '8px' : '12px', 
                justifyContent: window.innerWidth <= 768 ? 'stretch' : 'flex-end', 
                marginTop: window.innerWidth <= 768 ? '24px' : '32px',
                paddingTop: window.innerWidth <= 768 ? '20px' : '24px',
                borderTop: '2px solid #e5e7eb',
                flexDirection: window.innerWidth <= 768 ? 'column' : 'row'
              }}>
                <button
                  type="button"
                  onClick={onCancel}
                  style={{
                    padding: window.innerWidth <= 768 ? '12px 20px' : '14px 28px',
                    background: '#f3f4f6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontSize: window.innerWidth <= 768 ? '14px' : '15px',
                    fontWeight: 600,
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    flex: window.innerWidth <= 768 ? '1' : 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#e5e7eb';
                    e.target.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#f3f4f6';
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  <i className="fas fa-times" />
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: window.innerWidth <= 768 ? '12px 20px' : '14px 28px',
                    background: 'linear-gradient(135deg, #96ca2d 0%, #7ba818 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontSize: window.innerWidth <= 768 ? '14px' : '15px',
                    fontWeight: 600,
                    transition: 'all 0.3s ease',
                    flex: window.innerWidth <= 768 ? '1' : 'none',
                    boxShadow: '0 4px 12px rgba(150, 202, 45, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'linear-gradient(135deg, #7ba818 0%, #6a9a14 100%)';
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 16px rgba(150, 202, 45, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'linear-gradient(135deg, #96ca2d 0%, #7ba818 100%)';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 12px rgba(150, 202, 45, 0.3)';
                  }}
                >
                  <i className={`fas ${resource ? 'fa-save' : 'fa-plus-circle'}`} />
                  {resource ? 'Update' : 'Create'} resource
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  };

  return (
    <AdminLayout>
      {/* Notification Toast */}
      {notification && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          background: notification.type === 'success' ? '#10b981' : '#ef4444',
          color: 'white',
          padding: '16px 24px',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          minWidth: '300px',
          animation: 'slideInRight 0.3s ease-out'
        }}>
          <i className={`fas ${notification.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`} style={{ fontSize: '20px' }} />
          <span style={{ flex: 1, fontWeight: 500 }}>{notification.message}</span>
          <button
            onClick={() => setNotification(null)}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              fontSize: '18px',
              padding: '0',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <i className="fas fa-times" />
          </button>
        </div>
      )}
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #96ca2d 0%, #7fb800 100%)',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        color: 'white',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: '-20%',
          right: '-10%',
          width: '200px',
          height: '200px',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '50%',
          zIndex: 1
        }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 2, flexWrap: 'wrap', gap: '20px' }}>
          <div style={{ flex: 1 }}>
            <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 700 }}>Resource Management</h1>
            <p style={{ margin: '8px 0 0 0', opacity: 0.95 }}>Create, edit, and delete resources</p>
            {/* Statistiques */}
            <div style={{ 
              display: 'flex', 
              gap: window.innerWidth <= 768 ? '10px' : '16px', 
              marginTop: window.innerWidth <= 768 ? '16px' : '24px',
              flexWrap: 'wrap'
            }}>
              {/* Total ressources */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center',
                gap: window.innerWidth <= 768 ? '8px' : '12px',
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                padding: window.innerWidth <= 768 ? '10px 14px' : '12px 20px',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
              >
                <div style={{
                  width: window.innerWidth <= 768 ? '40px' : '48px',
                  height: window.innerWidth <= 768 ? '40px' : '48px',
                  borderRadius: '12px',
                  background: 'rgba(255, 255, 255, 0.25)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: window.innerWidth <= 768 ? '18px' : '20px',
                  color: 'white',
                  flexShrink: 0
                }}>
                  <i className="fas fa-folder-open" />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <div style={{ fontSize: window.innerWidth <= 768 ? '22px' : '28px', fontWeight: 700, color: 'white', lineHeight: '1.2' }}>
                    {resources.length}
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: window.innerWidth <= 768 ? '11px' : '13px', fontWeight: 500, marginTop: '2px' }}>
                    Total resources
                  </div>
                </div>
              </div>

              {/* Published */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center',
                gap: window.innerWidth <= 768 ? '8px' : '12px',
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                padding: window.innerWidth <= 768 ? '10px 14px' : '12px 20px',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
              >
                <div style={{
                  width: window.innerWidth <= 768 ? '40px' : '48px',
                  height: window.innerWidth <= 768 ? '40px' : '48px',
                  borderRadius: '12px',
                  background: 'rgba(255, 255, 255, 0.25)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: window.innerWidth <= 768 ? '18px' : '20px',
                  color: 'white',
                  flexShrink: 0
                }}>
                  <i className="fas fa-check-circle" />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <div style={{ fontSize: window.innerWidth <= 768 ? '22px' : '28px', fontWeight: 700, color: 'white', lineHeight: '1.2' }}>
                    {resources.filter(r => r.status === 'Publié').length}
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: window.innerWidth <= 768 ? '11px' : '13px', fontWeight: 500, marginTop: '2px' }}>
                    Published
                  </div>
                </div>
              </div>

              {/* Downloads */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center',
                gap: window.innerWidth <= 768 ? '8px' : '12px',
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                padding: window.innerWidth <= 768 ? '10px 14px' : '12px 20px',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
              >
                <div style={{
                  width: window.innerWidth <= 768 ? '40px' : '48px',
                  height: window.innerWidth <= 768 ? '40px' : '48px',
                  borderRadius: '12px',
                  background: 'rgba(255, 255, 255, 0.25)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: window.innerWidth <= 768 ? '18px' : '20px',
                  color: 'white',
                  flexShrink: 0
                }}>
                  <i className="fas fa-download" />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <div style={{ fontSize: window.innerWidth <= 768 ? '22px' : '28px', fontWeight: 700, color: 'white', lineHeight: '1.2' }}>
                    {resources.reduce((sum, r) => sum + (r.downloads || 0), 0)}
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: window.innerWidth <= 768 ? '11px' : '13px', fontWeight: 500, marginTop: '2px' }}>
                    Downloads
                  </div>
                </div>
              </div>
            </div>
          </div>
          <button
            onClick={() => {
              setEditingResource(null);
              setShowResourceForm(true);
            }}
            style={{
              padding: '12px 24px',
              background: 'rgba(255,255,255,0.2)',
              border: '2px solid white',
              borderRadius: '8px',
              color: 'white',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 600,
              transition: 'all 0.3s ease',
              alignSelf: 'flex-start'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'white';
              e.currentTarget.style.color = '#96ca2d';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
              e.currentTarget.style.color = 'white';
            }}
          >
            + New Resource
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '20px',
        marginBottom: '24px',
        border: '1px solid rgba(150, 202, 45, 0.1)',
        display: 'flex',
        gap: '16px',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        {/* Recherche */}
        <div style={{ flex: '1', minWidth: '250px' }}>
          <div style={{ position: 'relative' }}>
            <i className="fas fa-search" style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#9ca3af',
              fontSize: '14px'
            }} />
            <input
              type="text"
              placeholder="Search by title..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px 10px 38px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                transition: 'all 0.2s',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#96ca2d';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
              }}
            />
          </div>
        </div>

        {/* Filtre par date */}
        <div style={{ minWidth: '180px' }}>
          <div style={{ position: 'relative' }}>
            <i className="fas fa-calendar-alt" style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#9ca3af',
              fontSize: '14px'
            }} />
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px 10px 38px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                transition: 'all 0.2s',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#96ca2d';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
              }}
            />
          </div>
        </div>

        {/* Filtre par type */}
        <div style={{ minWidth: '180px' }}>
          <div style={{ position: 'relative' }}>
            <i className="fas fa-file" style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#9ca3af',
              fontSize: '14px',
              zIndex: 1
            }} />
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px 10px 38px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                transition: 'all 0.2s',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                backgroundColor: 'white',
                cursor: 'pointer',
                appearance: 'none',
                backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%23666\' d=\'M6 9L1 4h10z\'/%3E%3C/svg%3E")',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 12px center',
                paddingRight: '36px'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#96ca2d';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
              }}
            >
              <option value="">All types</option>
              {allTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Status filter */}
        <div style={{ minWidth: '180px' }}>
          <div style={{ position: 'relative' }}>
            <i className="fas fa-filter" style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#9ca3af',
              fontSize: '14px',
              zIndex: 1
            }} />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 36px 10px 38px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                backgroundColor: 'white',
                cursor: 'pointer',
                transition: 'all 0.2s',
                appearance: 'none',
                backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%23666\' d=\'M6 9L1 4h10z\'/%3E%3C/svg%3E")',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 12px center',
                boxShadow: 'none'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#96ca2d';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <option value="">All statuses</option>
              <option value="Publié">Published</option>
              <option value="Brouillon">Draft</option>
              {allStatuses
                .filter((status) => status !== 'Publié' && status !== 'Brouillon')
                .map(status => (
                  <option key={status} value={status}>{getStatusLabel(status)}</option>
                ))}
            </select>
          </div>
        </div>

        {/* Tri */}
        <div style={{ minWidth: '180px' }}>
          <div style={{ position: 'relative' }}>
            <i className="fas fa-sort" style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#9ca3af',
              fontSize: '14px',
              zIndex: 1
            }} />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px 10px 38px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                transition: 'all 0.2s',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                backgroundColor: 'white',
                cursor: 'pointer',
                appearance: 'none',
                backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%23666\' d=\'M6 9L1 4h10z\'/%3E%3C/svg%3E")',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 12px center',
                paddingRight: '36px'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#96ca2d';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
              }}
            >
              <option value="date">By date</option>
              <option value="title">By title</option>
              <option value="downloads">By downloads</option>
              <option value="status">By status</option>
            </select>
          </div>
        </div>

        {/* Ordre de tri */}
        <div style={{ minWidth: '140px' }}>
          <div style={{ position: 'relative' }}>
            <i className={`fas fa-sort-${sortOrder === 'asc' ? 'amount-up' : 'amount-down'}`} style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#9ca3af',
              fontSize: '14px',
              zIndex: 1
            }} />
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px 10px 38px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                transition: 'all 0.2s',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                backgroundColor: 'white',
                cursor: 'pointer',
                appearance: 'none',
                backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%23666\' d=\'M6 9L1 4h10z\'/%3E%3C/svg%3E")',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 12px center',
                paddingRight: '36px'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#96ca2d';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
              }}
            >
              <option value="desc">Descending</option>
              <option value="asc">Ascending</option>
            </select>
          </div>
        </div>

        {/* Bouton réinitialiser */}
        {(searchTerm || dateFilter || typeFilter || statusFilter) && (
          <button
            onClick={() => {
              setSearchTerm('');
              setDateFilter('');
              setTypeFilter('');
              setStatusFilter('');
            }}
            style={{
              padding: '10px 20px',
              background: '#f3f4f6',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              color: '#6b7280',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500,
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#e5e7eb';
              e.currentTarget.style.borderColor = '#d1d5db';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#f3f4f6';
              e.currentTarget.style.borderColor = '#e5e7eb';
            }}
          >
            <i className="fas fa-times" style={{ fontSize: '12px' }} />
            Reset
          </button>
        )}
      </div>

      {/* Resources List */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '24px',
        border: '1px solid rgba(150, 202, 45, 0.1)'
      }}>
        {sortedResources.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
            <p>
              {(searchTerm || dateFilter || typeFilter) 
                ? 'No resources match the selected filters.'
                : 'No resources yet.'}
            </p>
            {(searchTerm || dateFilter || typeFilter) ? (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setDateFilter('');
                  setTypeFilter('');
                }}
                style={{
                  padding: '12px 24px',
                  background: '#96ca2d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: 600,
                  marginTop: '16px'
                }}
              >
                Reset filters
              </button>
            ) : (
              <button
                onClick={() => {
                  setEditingResource(null);
                  setShowResourceForm(true);
                }}
                style={{
                  padding: '12px 24px',
                  background: '#96ca2d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: 600,
                  marginTop: '16px'
                }}
              >
                Create your first resource
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Indicateur de résultats */}
            {(searchTerm || dateFilter || typeFilter) && (
              <div style={{
                marginBottom: '20px',
                padding: '12px 16px',
                background: '#f0fdf4',
                border: '1px solid #86efac',
                borderRadius: '8px',
                color: '#166534',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <i className="fas fa-info-circle" style={{ fontSize: '16px' }} />
                <span>
                  {sortedResources.length} {sortedResources.length === 1 ? 'result found' : 'results found'}
                  {(searchTerm || dateFilter || typeFilter) && ' with filters applied'}
                </span>
              </div>
            )}
            <div style={{
              display: 'grid',
              gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: window.innerWidth <= 768 ? '16px' : '20px'
            }}>
              {currentResources.map(resource => (
                <div key={resource.id} style={{
                  background: 'white',
                  border: '2px solid #e5e7eb',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#96ca2d';
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(150, 202, 45, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
                }}
                >
                  <div style={{
                    position: 'absolute',
                    top: '0px',
                    left: '0px',
                    right: '0px',
                    height: '3px',
                    background: 'linear-gradient(135deg, rgb(150, 202, 45) 0%, rgb(127, 184, 0) 100%)',
                    transform: 'translateY(0px)',
                    borderColor: 'rgba(150, 202, 45, 0.1)',
                    zIndex: 2
                  }} />
                  <div style={{ 
                    padding: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: '200px'
                  }}>
                    {/* Icône type */}
                    <div style={{
                      width: '60px',
                      height: '60px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: '#f0f7e6',
                      borderRadius: '12px',
                      fontSize: '32px',
                      marginBottom: '16px'
                    }}>
                      {resource.type === 'PDF' && '📄'}
                      {resource.type === 'Excel' && '📊'}
                      {resource.type === 'Word' && '📝'}
                      {resource.type === 'Video' && '🎥'}
                      {resource.type === 'Link' && '🔗'}
                    </div>

                    {/* Titre - hauteur fixe */}
                    <div style={{ height: '44px', marginBottom: '16px', display: 'flex', alignItems: 'flex-start' }}>
                      <h3 style={{ 
                        margin: 0, 
                        fontSize: '18px', 
                        fontWeight: 600, 
                        color: '#1f2937',
                        lineHeight: '1.4',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>{resource.title}</h3>
                    </div>
                    
                    {/* Métadonnées - hauteur fixe */}
                    <div style={{ 
                      height: '26px', 
                      display: 'flex', 
                      gap: '12px', 
                      fontSize: '12px', 
                      color: '#9ca3af', 
                      marginBottom: '50px',
                      alignItems: 'center',
                      flexWrap: 'wrap'
                    }}>
                      <span><i className="fas fa-file" style={{ marginRight: '4px' }} />{resource.type}</span>
                      <span><i className="fas fa-download" style={{ marginRight: '4px' }} />{resource.downloads || 0} downloads</span>
                      {resource.createdAt && (
                        <span><i className="fas fa-calendar-alt" style={{ marginRight: '4px', color: '#96ca2d' }} />{resource.createdAt}</span>
                      )}
                      {resource.updatedAt && resource.updatedAt !== resource.createdAt && (
                        <span><i className="fas fa-calendar-check" style={{ marginRight: '4px', color: '#96ca2d' }} />Updated: {resource.updatedAt}</span>
                      )}
                      {!resource.createdAt && !resource.updatedAt && resource.date && (
                        <span><i className="fas fa-calendar-alt" style={{ marginRight: '4px', color: '#96ca2d' }} />{resource.date}</span>
                      )}
                      <span style={{
                        background: resource.status === 'Publié' ? '#dcfce7' : '#fef3c7',
                        color: resource.status === 'Publié' ? '#166534' : '#92400e',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontWeight: 600
                      }}>
                        {resource.status === 'Publié'
                          ? 'Published'
                          : resource.status === 'Brouillon'
                            ? 'Draft'
                            : resource.status}
                      </span>
                    </div>
                    
                    {/* Boutons - Design moderne */}
                    <div style={{ 
                      display: 'flex', 
                      gap: '8px', 
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      marginTop: '12px'
                    }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditResource(resource);
                        }}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '6px',
                          width: '32px',
                          height: '32px',
                          background: 'linear-gradient(135deg, #96ca2d 0%, #7ba818 100%)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          boxShadow: '0 2px 6px rgba(150, 202, 45, 0.3)',
                          position: 'relative',
                          overflow: 'hidden'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(150, 202, 45, 0.45)';
                          e.currentTarget.style.background = 'linear-gradient(135deg, #7ba818 0%, #6a9815 100%)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 2px 6px rgba(150, 202, 45, 0.3)';
                          e.currentTarget.style.background = 'linear-gradient(135deg, #96ca2d 0%, #7ba818 100%)';
                        }}
                        title="Edit"
                      >
                        <i className="fas fa-edit" style={{ fontSize: '14px' }} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setResourceToDelete(resource);
                        }}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '6px',
                          width: '32px',
                          height: '32px',
                          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          boxShadow: '0 2px 6px rgba(239, 68, 68, 0.25)',
                          position: 'relative',
                          overflow: 'hidden'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.4)';
                          e.currentTarget.style.background = 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 2px 6px rgba(239, 68, 68, 0.25)';
                          e.currentTarget.style.background = 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
                        }}
                        title="Delete"
                      >
                        <i className="fas fa-trash" style={{ fontSize: '14px' }} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {sortedResources.length > itemsPerPage && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 0,
                marginTop: '30px',
                flexWrap: 'wrap'
              }}>
                <div style={{ display: 'inline-flex', gap: 0 }}>
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    style={{
                      padding: '8px 10px',
                      minWidth: 36,
                      borderRadius: '6px 0 0 6px',
                      border: '1px solid #96ca2d',
                      background: currentPage === 1 ? '#f2f2f2' : '#96ca2d',
                      color: currentPage === 1 ? '#888' : '#fff',
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                      margin: 0,
                      borderRightWidth: 0
                    }}
                  >&lt;</button>
                  {(() => {
                    const buttons = [];
                    const renderBtn = (i, isFirst = false) => (
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        style={{
                          padding: '8px 10px',
                          minWidth: 36,
                          borderRadius: 0,
                          border: `1px solid ${currentPage === i ? '#96ca2d' : '#ddd'}`,
                          background: currentPage === i ? '#96ca2d' : '#fff',
                          color: currentPage === i ? '#fff' : '#333',
                          cursor: 'pointer',
                          margin: 0,
                          borderLeftWidth: isFirst ? '1px' : 0
                        }}
                      >
                        {i}
                      </button>
                    );

                    const shown = Math.min(totalPages, 3);
                    for (let i = 1; i <= shown; i++) {
                      buttons.push(renderBtn(i, i === 1));
                    }
                    if (totalPages > 3) {
                      buttons.push(
                        <span key="ell" style={{ padding: '8px 6px', border: '1px solid #ddd', borderLeftWidth: 0 }}>...</span>
                      );
                    }
                    return buttons;
                  })()}
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    style={{
                      padding: '8px 10px',
                      minWidth: 36,
                      borderRadius: '0 6px 6px 0',
                      border: '1px solid #96ca2d',
                      background: currentPage === totalPages ? '#f2f2f2' : '#96ca2d',
                      color: currentPage === totalPages ? '#888' : '#fff',
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                      margin: 0,
                      borderLeftWidth: 0
                    }}
                  >&gt;</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Form Modal */}
      {showResourceForm && (
        <ResourceForm
          resource={editingResource}
          onSubmit={handleResourceSubmit}
          onCancel={() => {
            setShowResourceForm(false);
            setEditingResource(null);
          }}
        />
      )}

      {/* Popup de confirmation de suppression */}
      {resourceToDelete && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001,
            padding: window.innerWidth <= 768 ? '10px' : '20px',
            paddingTop: window.innerWidth <= 768 ? '74px' : '20px',
            overflowY: 'auto'
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: window.innerWidth <= 768 ? '12px' : '16px',
              maxWidth: window.innerWidth <= 768 ? '100%' : '450px',
              width: '100%',
              position: 'relative',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Barre de gradient */}
            <div style={{
              position: 'absolute',
              top: '0px',
              left: '0px',
              right: '0px',
              height: '4px',
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              borderRadius: window.innerWidth <= 768 ? '12px 12px 0 0' : '16px 16px 0 0'
            }} />
            
            {/* Contenu de la popup */}
            <div style={{ padding: window.innerWidth <= 768 ? '16px' : '24px' }}>
              {/* Icône d'avertissement */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                marginBottom: window.innerWidth <= 768 ? '16px' : '20px'
              }}>
                <div style={{
                  width: window.innerWidth <= 768 ? '56px' : '64px',
                  height: window.innerWidth <= 768 ? '56px' : '64px',
                  borderRadius: '50%',
                  background: '#fef2f2',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '3px solid #fee2e2'
                }}>
                  <i className="fas fa-exclamation-triangle" style={{
                    fontSize: window.innerWidth <= 768 ? '28px' : '32px',
                    color: '#ef4444'
                  }} />
                </div>
              </div>

              {/* Titre */}
              <h2 style={{
                margin: 0,
                fontSize: window.innerWidth <= 768 ? '18px' : '20px',
                fontWeight: 700,
                color: '#1f2937',
                textAlign: 'center',
                marginBottom: '12px'
              }}>
                Delete Resource
              </h2>

              {/* Message */}
              <p style={{
                margin: 0,
                fontSize: window.innerWidth <= 768 ? '13px' : '14px',
                color: '#6b7280',
                textAlign: 'center',
                lineHeight: '1.6',
                marginBottom: window.innerWidth <= 768 ? '20px' : '24px'
              }}>
                Are you sure you want to delete the resource <strong>"{resourceToDelete.title}"</strong>? This action is irreversible.
              </p>

              {/* Boutons */}
              <div style={{
                display: 'flex',
                gap: window.innerWidth <= 768 ? '8px' : '12px',
                flexDirection: window.innerWidth <= 768 ? 'column' : 'row'
              }}>
                <button
                  onClick={() => setResourceToDelete(null)}
                  style={{
                    flex: 1,
                    padding: '12px 20px',
                    background: '#f3f4f6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 600,
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#e5e7eb';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#f3f4f6';
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteResource}
                  style={{
                    flex: 1,
                    padding: '12px 20px',
                    background: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 600,
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#dc2626';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#ef4444';
                  }}
                >
                  <i className="fas fa-trash" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default Resources;


